import React, { useMemo, useState } from 'react';
import {
  getGuessState,
  recordGuess,
  matchAnswers,
  markContributed,
} from '../game/meta/progression';
import { buildGuessOptions } from '../game/voxel/guessOptions.js';
import { IconWand } from './AtelierIcons';

function gloryMult(pct) {
  if (pct < 25) return '5×';
  if (pct < 50) return '3×';
  if (pct < 75) return '1.5×';
  return '1×';
}

/**
 * Early guess via name chip sheet (no typing).
 * Locked until reconstruction has started (completionPct > 0).
 */
const BlindGuessPanel = ({ model, category, completionPct, disabled, onFeedback, date }) => {
  const [state, setState] = useState(() => getGuessState(date));
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState(null); // { ok, name, mult } | null

  const dayKey = date || new Date().toISOString().split('T')[0];
  const { options, correct } = useMemo(
    () => buildGuessOptions(model, dayKey, 10),
    [model, dayKey]
  );

  const rejected = useMemo(() => {
    const set = new Set();
    for (const s of state.submitted || []) {
      if (!s.correct && s.text) set.add(String(s.text).toLowerCase());
    }
    return set;
  }, [state.submitted]);

  if (state.correct) {
    const reward = state.reward;
    const mult = reward?.gloryMult || '1×';
    const pct =
      reward?.completionPct != null ? `${Math.round(reward.completionPct)}% reconstructed` : null;
    return (
      <div className="guess-panel guess-panel--success">
        <div className="guess-success-title">You guessed correctly</div>
        <div className="guess-success-reward">
          Earned <strong>Glory {mult}</strong>
          {pct ? ` · guessed at ${pct}` : ''}
        </div>
        <div className="guess-success-body">
          Prediction locked privately · object name stays hidden until 100% reconstruction
        </div>
        <div className="guess-success-perk">Also unlocked: collection credit for today</div>
      </div>
    );
  }

  if (disabled) {
    return (
      <div className="guess-panel guess-panel--done">
        <div className="guess-panel-title">
          <IconWand className="atelier-inline-ico" />
          BLIND GUESS
        </div>
        Mystery fully restored — name revealed above. Blind guess is closed for today.
      </div>
    );
  }

  if (completionPct <= 0) {
    return (
      <div className="guess-panel guess-panel--locked">
        <div className="guess-panel-title">
          <IconWand className="atelier-inline-ico" />
          BLIND GUESS
        </div>
        Solve a slice so the reconstruction appears — then pick a name from the list. Category:{' '}
        <em>{category || model?.category || '…'}</em>
      </div>
    );
  }

  if (state.remaining <= 0) {
    return (
      <div className="guess-panel guess-panel--done">
        No guess attempts left today — keep reconstructing.
        {import.meta.env.DEV && (
          <div style={{ marginTop: 6, opacity: 0.7, fontSize: '0.72rem' }}>
            Dev: use Debug → RESET GUESSES (reload alone keeps the daily limit).
          </div>
        )}
      </div>
    );
  }

  const multHint = `${gloryMult(completionPct)} Glory`;

  const pickName = (name) => {
    if (rejected.has(String(name).toLowerCase())) return;
    if (state.remaining <= 0) {
      onFeedback?.('No guess attempts left today');
      return;
    }
    const isCorrect = matchAnswers(name, model.answers || []) || name === correct;
    if (isCorrect) {
      const mult = gloryMult(completionPct);
      const next = recordGuess(name, true, dayKey, {
        gloryMult: mult,
        completionPct,
      });
      setState(next);
      markContributed(dayKey);
      setResult({ ok: true, name, mult });
      onFeedback?.(`Correct! Glory ${mult} locked in`);
      setTimeout(() => {
        setOpen(false);
        setResult(null);
      }, 2200);
    } else {
      const next = recordGuess(name, false, dayKey);
      setState(next);
      setResult({ ok: false, name });
      onFeedback?.(`Not ${name} · ${next.remaining} left`);
      setTimeout(() => setResult(null), 1400);
      if (next.remaining <= 0) {
        setTimeout(() => setOpen(false), 1400);
      }
    }
  };

  return (
    <div className="guess-panel">
      <div className="guess-panel-row">
        <div>
          <div className="guess-panel-title">
            <IconWand className="atelier-inline-ico" />
            BLIND GUESS
          </div>
          <div className="guess-panel-meta">
            {state.remaining}/3 left · {multHint} · {category || model?.category}
          </div>
        </div>
        <button type="button" className="atelier-btn-primary guess-open-btn" onClick={() => setOpen(true)}>
          Guess
        </button>
      </div>
      <div className="guess-panel-hint">
        Tap Guess and pick from the name grid — based on the reconstruction above.
      </div>
      {state.submitted?.length > 0 && (
        <div className="guess-panel-sealed">
          Attempts used: {state.submitted.length} · wrong picks stay crossed out
        </div>
      )}

      {open && (
        <div
          className="guess-sheet-backdrop atelier-guess-backdrop"
          role="presentation"
          onClick={() => !result?.ok && setOpen(false)}
        >
          <div
            className="guess-sheet atelier-guess-sheet"
            role="dialog"
            aria-label="Guess the mystery"
            onClick={(e) => e.stopPropagation()}
          >
            {result?.ok ? (
              <div className="guess-result guess-result--ok atelier-guess-ok" role="status">
                <div className="guess-result-big">Correct</div>
                <div className="guess-result-name">{result.name}</div>
                <div className="guess-result-detail">
                  Earned Glory {result.mult} · name stays secret until 100%
                </div>
              </div>
            ) : (
              <>
                <div className="guess-sheet-header">
                  <div>
                    <div className="guess-sheet-kicker">
                      <IconWand className="atelier-inline-ico" />
                      What is it?
                    </div>
                    <div className="guess-sheet-sub">
                      {state.remaining} attempt{state.remaining === 1 ? '' : 's'} left · category{' '}
                      {category || model?.category}
                    </div>
                  </div>
                  <button type="button" className="atelier-chip-btn" onClick={() => setOpen(false)}>
                    Close
                  </button>
                </div>
                {result && !result.ok && (
                  <div className="guess-result guess-result--bad atelier-guess-bad" role="status">
                    Not {result.name} — try another
                  </div>
                )}
                <div className="guess-name-grid">
                  {options.map((name) => {
                    const dead = rejected.has(String(name).toLowerCase());
                    return (
                      <button
                        key={name}
                        type="button"
                        className={`guess-name-chip atelier-guess-chip${dead ? ' is-rejected' : ''}`}
                        disabled={dead || !!result}
                        onClick={() => pickName(name)}
                      >
                        {name}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BlindGuessPanel;
